import { useCallback, useMemo, useReducer } from 'react';

/**
 * Shared state + logic for the "Teams & Users" selection used across the app's
 * filters.
 *
 * The selection is a union of individual users (`userIds`) and whole teams
 * (`teamIds`), plus `noUsers` and `isCurrentUser` flags: `noUsers` is for
 * the "unassigned" option and is genuinely distinct from an empty selection
 * (which means "no filter") so it is stored, while `isCurrentUser` means the
 * selection is exactly the current user with no teams so it is derived.
 *
 * Selecting users/teams and `noUsers`/`isCurrentUser` are mutually exclusive.
 */

/**
 * Maps the legacy single-owner preference to the multi-user `userIds` array.
 * `-1` is the legacy "all users" sentinel and resolves to an empty selection.
 */
export const legacyUserToUserIds = (selectedUser?: number | null): number[] =>
  selectedUser != null && selectedUser !== -1 ? [selectedUser] : [];

/**
 * Map the multi-user selection back to the legacy single-owner field: only a
 * single plain user maps back; multi/team selections clear the legacy field.
 */
export const userIdsToLegacyUser = (
  userIds: number[],
  teamIds: number[],
): number | undefined =>
  userIds.length === 1 && teamIds.length === 0 ? userIds[0] : undefined;

/** True when the selection is exactly the current user and no teams. */
export const isCurrentUserSelection = (
  userIds: number[],
  teamIds: number[],
  currentUserId?: number,
): boolean =>
  currentUserId != null &&
  userIds.length === 1 &&
  userIds[0] === currentUserId &&
  teamIds.length === 0;

/**
 * Number of applied filters: `noUsers` counts as one, otherwise each selected
 * user and team counts, including the current-user selection.
 */
export const appliedUsersFiltersCount = (
  userIds: number[],
  teamIds: number[],
  noUsers: boolean,
): number => {
  if (noUsers) {
    return 1;
  }
  return userIds.length + teamIds.length;
};

export interface UsersFilterState {
  userIds: number[];
  teamIds: number[];
  noUsers: boolean;
}

const EMPTY_STATE: UsersFilterState = {
  userIds: [],
  teamIds: [],
  noUsers: false,
};

type UsersFilterAction =
  | { type: 'setUsers'; userIds?: number[]; teamIds?: number[] }
  | { type: 'setNoUsers'; noUsers: boolean }
  | { type: 'toggleCurrentUser'; currentUserId?: number; enabled?: boolean }
  | { type: 'clear' };

const usersFilterReducer = (
  state: UsersFilterState,
  action: UsersFilterAction,
): UsersFilterState => {
  switch (action.type) {
    case 'setUsers':
      return {
        userIds: action.userIds ?? state.userIds,
        teamIds: action.teamIds ?? state.teamIds,
        noUsers: false,
      };
    case 'setNoUsers':
      return action.noUsers
        ? { userIds: [], teamIds: [], noUsers: true }
        : EMPTY_STATE;
    case 'toggleCurrentUser': {
      const enabled =
        action.enabled ??
        !isCurrentUserSelection(
          state.userIds,
          state.teamIds,
          action.currentUserId,
        );
      return enabled && action.currentUserId != null
        ? { userIds: [action.currentUserId], teamIds: [], noUsers: false }
        : EMPTY_STATE;
    }
    case 'clear':
      return EMPTY_STATE;
    default:
      return state;
  }
};

interface UseUsersFilterParams {
  initialUserIds?: number[];
  initialTeamIds?: number[];
  initialNoUsers?: boolean;
  /**
   * Seed the selection as the current user (equivalent to
   * `initialUserIds: [currentUserId]`). Takes precedence over the other
   * `initial*` values and requires `currentUserId`.
   */
  initialIsCurrentUser?: boolean;
  currentUserId?: number;
}

export interface UseUsersFilterResult extends UsersFilterState {
  isCurrentUser: boolean;
  /** Whether any user or team is selected. */
  hasUsers: boolean;
  appliedFiltersCount: number;
  setUserIds: (userIds: number[]) => void;
  setTeamIds: (teamIds: number[]) => void;
  setNoUsers: (noUsers: boolean) => void;
  setUsers: (userIds: number[], teamIds: number[]) => void;
  toggleCurrentUser: (enabled?: boolean) => void;
  clear: () => void;
}

export const useUsersFilter = ({
  initialUserIds = [],
  initialTeamIds = [],
  initialNoUsers = false,
  initialIsCurrentUser = false,
  currentUserId,
}: UseUsersFilterParams = {}): UseUsersFilterResult => {
  const [state, dispatch] = useReducer(
    usersFilterReducer,
    undefined,
    (): UsersFilterState =>
      initialIsCurrentUser && currentUserId != null
        ? { userIds: [currentUserId], teamIds: [], noUsers: false }
        : {
            userIds: initialUserIds,
            teamIds: initialTeamIds,
            noUsers: initialNoUsers,
          },
  );

  const setUsers = useCallback(
    (userIds: number[], teamIds: number[]) =>
      dispatch({ type: 'setUsers', userIds, teamIds }),
    [],
  );

  const setUserIds = useCallback(
    (userIds: number[]) => dispatch({ type: 'setUsers', userIds }),
    [],
  );

  const setTeamIds = useCallback(
    (teamIds: number[]) => dispatch({ type: 'setUsers', teamIds }),
    [],
  );

  const setNoUsers = useCallback(
    (noUsers: boolean) => dispatch({ type: 'setNoUsers', noUsers }),
    [],
  );

  const toggleCurrentUser = useCallback(
    (enabled?: boolean) =>
      dispatch({ type: 'toggleCurrentUser', currentUserId, enabled }),
    [currentUserId],
  );

  const clear = useCallback(() => dispatch({ type: 'clear' }), []);

  return useMemo(
    () => ({
      ...state,
      isCurrentUser: isCurrentUserSelection(
        state.userIds,
        state.teamIds,
        currentUserId,
      ),
      hasUsers: state.userIds.length > 0 || state.teamIds.length > 0,
      appliedFiltersCount: appliedUsersFiltersCount(
        state.userIds,
        state.teamIds,
        state.noUsers,
      ),
      setUserIds,
      setTeamIds,
      setNoUsers,
      setUsers,
      toggleCurrentUser,
      clear,
    }),
    [
      state,
      currentUserId,
      setUserIds,
      setTeamIds,
      setNoUsers,
      setUsers,
      toggleCurrentUser,
      clear,
    ],
  );
};
